import { useState, useCallback, useRef, useEffect } from "react";

export interface StepDef {
  /** Unique step key, e.g. "identity", "vehicle" */
  key: string;
  /** Display label for progress indicator, e.g. "Member Verification" */
  label: string;
  /**
   * Optional validation function.
   * Return `true` if the step is valid and the user can advance.
   * If omitted, the step is always considered valid.
   */
  validate?: () => boolean;
}

export function useStepWizard(steps: StepDef[]) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevIndex = useRef(currentStepIndex);

  // Scroll to top of form when step changes
  useEffect(() => {
    if (prevIndex.current !== currentStepIndex) {
      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      prevIndex.current = currentStepIndex;
    }
  }, [currentStepIndex]);

  const currentStep = steps[currentStepIndex]!;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const goNext = useCallback(() => {
    const step = steps[currentStepIndex];
    if (!step) return;
    const valid = step.validate ? step.validate() : true;
    if (!valid) return;
    setCompletedSteps((prev) => new Set(prev).add(currentStepIndex));
    setCurrentStepIndex((i) => Math.min(i + 1, steps.length - 1));
  }, [steps, currentStepIndex]);

  const goBack = useCallback(() => {
    setCurrentStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= steps.length) return;
      // Allow navigating to any completed step or the next uncompleted one
      const maxReachable = Math.max(...Array.from(completedSteps), -1) + 1;
      if (index <= maxReachable) {
        setCurrentStepIndex(index);
      }
    },
    [steps.length, completedSteps],
  );

  return {
    currentStepIndex,
    currentStep,
    isFirstStep,
    isLastStep,
    completedSteps,
    goNext,
    goBack,
    goTo,
    containerRef,
  };
}
